import { delayMs } from '@listening/utils/ts-utils';
import AndroidIcon from '@listening/assets/astro/icons/android.svg?react';
import AppleIcon from '@listening/assets/astro/icons/apple.svg?react';
import ChromeExtIcon from '@listening/assets/astro/icons/chrome-ext.svg?react';
import UploadIcon from '@listening/assets/astro/icons/upload.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import * as m from '@/paraglide/messages';
import { usePostPurchaseStep } from '../post-purchase.store';
import { POST_PURCHASE_TOTAL_PAGES } from '../post-purchase-constants';

// TODO: Add desktop download link when desktop app is updated
// const desktopAppLink = 'https://www.listening.com/app/home';
const googlePlayLink =
  'https://play.google.com/store/apps/details?id=io.listening.main';
const appStoreLink =
  'https://apps.apple.com/us/app/listening-read-on-the-go/id1635430768';
const chromeExtLink =
  'https://chromewebstore.google.com/detail/listeningcom/amjjglmomeodglgclcnjdjbhhcfnbnpd';

const shootConfetti = async () => {
  if (typeof window === 'undefined') return;

  // dynamic-import -- canvas-confetti touches the DOM at import time and fires once on this step, so keep it out of the route bundle.
  const { default: confetti } = await import('canvas-confetti');
  await confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.4 },
    colors: ['#FFD700', '#FFA500', '#FF69B4', '#00FF00', '#87CEEB'],
  });
};

export function Step4Welcome() {
  const hasShownInitialConfetti = useRef(false);
  const currentStep = usePostPurchaseStep();

  useEffect(() => {
    // Show confetti only when step 4 becomes active and hasn't shown before
    if (currentStep !== POST_PURCHASE_TOTAL_PAGES - 1) return;
    if (hasShownInitialConfetti.current) return;
    hasShownInitialConfetti.current = true;
    // Shoot confetti after carousel animation
    void delayMs(400).then(() => void shootConfetti());
  }, [currentStep]);

  return (
    <ScrollArea className='w-full'>
      <div className='mt-12 flex h-screen items-center justify-center sm:mt-0'>
        <div className='flex w-full max-w-[500px] flex-col items-center gap-4 px-4'>
          <div className='relative'>
            <div className='relative'>
              {/* Left side stars */}
              <span className='absolute -top-8 -left-12 text-lg text-purple-400 select-none'>
                *
              </span>
              <span className='absolute top-0 -left-24 text-lg text-blue-400 select-none'>
                *
              </span>
              <span className='absolute top-8 -left-8 text-lg text-blue-400 select-none'>
                *
              </span>

              {/* Right side stars */}
              <span className='absolute -top-4 -right-12 text-lg text-blue-300 select-none'>
                *
              </span>
              <span className='absolute top-4 -right-24 text-lg text-purple-300 select-none'>
                *
              </span>
              <span className='absolute top-8 -right-8 text-lg text-pink-300 select-none'>
                *
              </span>
              <span
                className='inline-block cursor-default text-5xl transition-all duration-300 hover:scale-125 hover:rotate-12'
                onMouseEnter={() => void shootConfetti()}
              >
                🎉
              </span>
            </div>
          </div>
          <h1 className='text-3xl font-bold sm:text-6xl'>
            {m.post_purchase_congratulations()}
          </h1>
          <p className='text-sm font-medium text-black sm:text-xl'>
            {m.post_purchase_next_upload()}
          </p>

          <Button
            asChild
            className='mt-2 flex h-auto items-center justify-center gap-2 rounded-full bg-black px-4 py-3 text-white transition-all duration-300 hover:bg-gray-900 hover:shadow-lg sm:px-6 sm:py-4'
          >
            <Link to='/app/home'>
              {m.post_purchase_upload_cta()}
              <UploadIcon />
            </Link>
          </Button>

          <div className='mt-14 flex flex-col items-center gap-4 px-4'>
            <p className='text-center text-sm text-black sm:text-base sm:text-nowrap'>
              {m.post_purchase_check_inbox()}
            </p>

            <div className='flex w-full flex-wrap justify-center gap-4 sm:gap-5'>
              <SecondaryActionLink
                href={appStoreLink}
                icon={<AppleIcon />}
                label={m.post_purchase_ios_app()}
              />
              <SecondaryActionLink
                href={googlePlayLink}
                icon={<AndroidIcon />}
                label={m.post_purchase_android_app()}
              />
              <SecondaryActionLink
                href={chromeExtLink}
                icon={<ChromeExtIcon />}
                label={m.post_purchase_chrome_extension()}
              />
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
function SecondaryActionLink(props: {
  href: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Button
      asChild
      className='flex h-auto w-[130px] flex-col items-center gap-2 rounded-lg bg-transparent p-2 font-normal transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:bg-gray-50 hover:shadow-xl'
    >
      <a className='flex flex-col items-center' href={props.href}>
        <div className='flex size-8 items-center justify-center'>
          {props.icon}
        </div>
        <span className='text-center text-xs whitespace-nowrap text-neutral-665 sm:text-sm'>
          {props.label}
        </span>
      </a>
    </Button>
  );
}
